/**
 * ToastSystem.ts
 * Transient on-screen notifications — wave warnings, venue claims,
 * Song Drop alerts, signal band changes, etc.
 *
 * Toasts slide in from just below the HUD and expire with a fade-out.
 * A maximum of 3 toasts are stacked at once; excess are silently dropped.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';

// ── Types ─────────────────────────────────────────────────────────────────
export type ToastType = 'info' | 'warning' | 'success' | 'danger';

interface ToastEntry {
  root:     Container;
  lifetime: number;
  elapsed:  number;
  /** Pixel width the toast was drawn at (capped to screen width). */
  width:    number;
  /** Actual rendered height — 36px for single-line, taller when text wraps. */
  height:   number;
}

// ── Palette ───────────────────────────────────────────────────────────────
const TYPE_COLOUR: Record<ToastType, number> = {
  info:    0x00ffcc,
  warning: 0xffaa00,
  success: 0x44cc66,
  danger:  0xff3333,
};

// ── Layout constants ───────────────────────────────────────────────────────
const TOAST_W      = 380;
const TOAST_H_MIN  = 36;   // single-line height
const TOAST_PAD_V  = 16;   // top + bottom padding inside the box
const TOAST_Y_BASE = 56;   // top edge of first toast (just under HUD)
const TOAST_GAP    = 8;    // vertical gap between stacked toasts
const ENTER_TIME   = 0.18; // seconds to fade in
const LEAVE_TIME   = 0.30; // seconds to fade out
const MAX_STACK    = 3;    // maximum simultaneous toasts

export class ToastSystem {
  private readonly container = new Container();
  private readonly active: ToastEntry[] = [];
  private screenW = 1280;

  constructor(ui: Container) {
    ui.addChild(this.container);
  }

  resize(w: number, _h: number): void {
    this.screenW = w;
    this.reposition();
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Display a toast notification.
   * @param message  Text to show (keep it short — one clear sentence)
   * @param type     Visual variant: 'info' | 'warning' | 'success' | 'danger'
   * @param duration How long the toast stays fully visible (seconds; default 3.5s)
   */
  show(message: string, type: ToastType = 'info', duration = 3.5): void {
    if (this.active.length >= MAX_STACK) return;

    const colour   = TYPE_COLOUR[type];
    const hex      = `#${colour.toString(16).padStart(6, '0')}`;
    const tw       = Math.min(TOAST_W, this.screenW - 16);
    const maxTextW = tw - 26; // 14px left pad + 12px right margin

    // Build text with word wrap so long messages flow to a second line
    // instead of being truncated.
    const txt = new Text(message, new TextStyle({
      fontFamily:    'Menlo, Consolas, monospace',
      fontSize:       11,
      fontWeight:    'bold',
      fill:           hex,
      letterSpacing:  0.5,
      wordWrap:       true,
      wordWrapWidth:  maxTextW,
      lineHeight:     16,
    }));
    txt.anchor.set(0, 0);
    txt.eventMode = 'none';

    // Size the box to fit the text; always at least TOAST_H_MIN tall.
    const th   = Math.max(TOAST_H_MIN, Math.ceil(txt.height) + TOAST_PAD_V);
    const textY = Math.floor((th - txt.height) / 2);
    txt.position.set(14, textY);

    const root = new Container();

    // Background panel
    const bg = new Graphics();
    bg.beginFill(0x080b10, 0.92);
    bg.lineStyle(1, colour, 0.5);
    bg.drawRoundedRect(0, 0, tw, th, 3);
    bg.endFill();
    bg.lineStyle(0);
    root.addChild(bg);

    // Left accent bar
    const bar = new Graphics();
    bar.beginFill(colour, 0.85);
    bar.drawRect(0, 0, 3, th);
    bar.endFill();
    root.addChild(bar);

    root.addChild(txt);
    root.alpha = 0;
    this.container.addChild(root);

    this.active.push({ root, lifetime: duration, elapsed: 0, width: tw, height: th });
    this.reposition();
  }

  // ── Game loop ─────────────────────────────────────────────────────────────

  update(delta: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const t = this.active[i];
      t.elapsed += delta;

      if (t.elapsed < ENTER_TIME) {
        // Fade in
        t.root.alpha = t.elapsed / ENTER_TIME;
      } else if (t.elapsed >= t.lifetime - LEAVE_TIME) {
        // Fade out
        const rem    = t.lifetime - t.elapsed;
        t.root.alpha = Math.max(0, rem / LEAVE_TIME);
      } else {
        t.root.alpha = 1;
      }

      if (t.elapsed >= t.lifetime) {
        this.container.removeChild(t.root);
        this.active.splice(i, 1);
      }
    }

    this.reposition();
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private reposition(): void {
    let y = TOAST_Y_BASE;
    for (const entry of this.active) {
      const cx = Math.round((this.screenW - entry.width) / 2);
      entry.root.position.set(cx, y);
      y += entry.height + TOAST_GAP;
    }
  }
}
